/**
 * Property type taxonomy — single source of truth for the frontend.
 *
 * Mirrored from backend/property_types.py. Keep both in sync.
 *
 * 12-item lean taxonomy:
 *   - 8 residential
 *   - 2 land (with/without planning)
 *   - 1 commercial
 *   - 1 other
 *
 * `isNewHome` is a separate boolean flag — not in this enum.
 */

export const PROPERTY_TYPES = [
  // Residential
  { slug: 'detached',           label: 'Detached House',      group: 'residential' },
  { slug: 'semi-detached',      label: 'Semi-Detached House', group: 'residential' },
  { slug: 'terraced',           label: 'Terraced House',      group: 'residential' },
  { slug: 'bungalow',           label: 'Bungalow',            group: 'residential' },
  { slug: 'townhouse',          label: 'Townhouse',           group: 'residential' },
  { slug: 'apartment',          label: 'Apartment',           group: 'residential' },
  { slug: 'duplex',             label: 'Duplex',              group: 'residential' },
  { slug: 'studio',             label: 'Studio',              group: 'residential' },
  // Land (sale only)
  { slug: 'site-with-planning', label: 'Site — With planning permission', group: 'land' },
  { slug: 'site-no-planning',   label: 'Site — No planning permission',   group: 'land' },
  // Commercial / catch-all
  { slug: 'commercial',         label: 'Commercial',          group: 'commercial' },
  { slug: 'other',              label: 'Other / Unclassified', group: 'other' },
];

export const PROPERTY_GROUPS = [
  { id: 'residential', label: 'Residential' },
  { id: 'land',        label: 'Land' },
  { id: 'commercial',  label: 'Commercial' },
  { id: 'other',       label: 'Other' },
];

const LEGACY_SLUG_MAP = {
  house: 'detached',
  site: 'site-no-planning',
  land: 'site-no-planning',
};

const VALID = new Set(PROPERTY_TYPES.map((t) => t.slug));

export function normalisePropertyType(slug) {
  if (!slug) return null;
  const s = String(slug).trim().toLowerCase().replace(/_/g, '-');
  if (s === '' || s === 'all' || s === 'any') return null;
  if (VALID.has(s)) return s;
  return LEGACY_SLUG_MAP[s] || null;
}

export function labelForPropertyType(slug) {
  const norm = normalisePropertyType(slug);
  const hit = PROPERTY_TYPES.find((t) => t.slug === (norm || slug));
  return hit ? hit.label : (slug || '');
}

/**
 * Which types should appear in the picker for a given listing mode.
 * `share` / `rent` listings can't be land or commercial.
 */
export function typesForMode(mode) {
  const m = (mode || 'sale').toLowerCase();
  const groups = m === 'rent' || m === 'share'
    ? new Set(['residential', 'other'])
    : new Set(['residential', 'land', 'commercial', 'other']);
  return PROPERTY_TYPES.filter((t) => groups.has(t.group));
}

/** Groups the types by their `group` field for rendering with sub-headers. */
export function groupedTypesForMode(mode) {
  const filtered = typesForMode(mode);
  return PROPERTY_GROUPS
    .map((g) => ({ ...g, items: filtered.filter((t) => t.group === g.id) }))
    .filter((g) => g.items.length > 0);
}
